import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Star, MessageCircle, Briefcase, Calendar,
  ShieldCheck, Award, ChevronRight, Clock, FileText
} from 'lucide-react';
import { apiBaseUrl } from '../config/runtime';
import { categoryLabel } from '../i18n/categoryNames';
import ReviewList from '../components/ReviewList';
import ExpertAnalysisChatbot from '../components/ExpertAnalysisChatbot';

interface FreelancerProfile {
  id: number;
  nickname: string;
  name: string;
  profile_image?: string;
  bio?: string;
  title?: string;
  skills?: string;
  experience_years?: number;
  hourly_rate?: number;
  portfolio_url?: string;
  member_since: string;
  expert_verified?: boolean;
  insurance_verified?: boolean;
  total_services: number;
  total_reviews: number;
  average_rating: number;
  services: Array<{
    id: number;
    title: string;
    price_basic: number;
    delivery_days_basic: number;
    category_name: string;
    rating_average: number;
  }>;
}

const INDIGO = '#4F46E5';

const FreelancerProfilePage: React.FC = () => {
  const { nickname } = useParams<{ nickname: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState<FreelancerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('[FreelancerProfile] 프로필 로드:', nickname);
    if (!nickname) return;
    fetch(apiBaseUrl(`/freelancers/${nickname}`))
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setProfile(data.data);
          console.log('[FreelancerProfile] 로드 완료:', data.data.nickname);
        } else {
          setError(data.message || t('freelancer.notFound'));
        }
      })
      .catch(() => setError(t('freelancer.loadError')))
      .finally(() => setLoading(false));
  }, [nickname, t]);

  const handleContact = () => {
    console.log('[FreelancerProfile] 채팅 시작 클릭');
    navigate('/chat');
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', { year: 'numeric', month: 'long' });

  const parseSkills = (raw?: string): string[] => {
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', color: '#BBB', fontSize: 14 }}>
      {t('common.loading')}
    </div>
  );

  if (error || !profile) return (
    <div style={{ maxWidth: 500, margin: '80px auto', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#333', marginBottom: 8 }}>{t('freelancer.notFoundTitle')}</h2>
      <p style={{ color: '#AAA', marginBottom: 24 }}>{error}</p>
      <button onClick={() => navigate('/services')} style={{
        padding: '10px 24px', background: INDIGO, color: '#fff',
        border: 'none', borderRadius: 9, cursor: 'pointer',
        fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
      }}>{t('freelancer.viewExperts')}</button>
    </div>
  );

  const skills = parseSkills(profile.skills);
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 14,
    border: '1px solid #F0F0F0', padding: '22px 24px', marginBottom: 16,
  };

  return (
    <div style={{ background: '#F8F8FF', minHeight: '100vh', padding: '32px 20px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* 브레드크럼 */}
        <div style={{ fontSize: 13, color: '#AAA', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span onClick={() => navigate('/services')} style={{ cursor: 'pointer', color: INDIGO }}>{t('freelancer.breadcrumb')}</span>
          <ChevronRight size={13} color="#CCC" />
          <span>{profile.nickname}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>

          {/* ── 왼쪽 ── */}
          <div>
            {/* 프로필 헤더 */}
            <div style={{ ...card, display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: INDIGO, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, fontWeight: 800, flexShrink: 0, overflow: 'hidden',
              }}>
                {profile.profile_image
                  ? <img src={profile.profile_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : profile.nickname.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111', letterSpacing: '-0.5px', margin: 0 }}>
                    {profile.nickname}
                  </h1>
                  {profile.insurance_verified && (
                    <span
                      onClick={() => user && user.id !== profile.id ? navigate(`/report/${profile.id}`) : undefined}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 9px', background: '#ECFDF5', color: '#059669', borderRadius: 100, fontSize: 12, fontWeight: 600, cursor: user && user.id !== profile.id ? 'pointer' : 'default' }}
                      title={user && user.id !== profile.id ? t('freelancer.reportTooltip') : t('freelancer.insuranceDone')}
                    >
                      <ShieldCheck size={12} />{t('freelancer.insuranceBadge')}
                    </span>
                  )}
                </div>
                {profile.title && (
                  <div style={{ fontSize: 15, color: '#555', marginBottom: 8 }}>{profile.title}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#888' }}>
                    <Star size={13} fill="#F59E0B" stroke="#F59E0B" />
                    <span>{Number(profile.average_rating || 0).toFixed(1)}</span>
                    <span style={{ color: '#CCC' }}>{t('freelancer.reviewsN', { n: profile.total_reviews })}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#888' }}>
                    <Briefcase size={13} />
                    <span>{t('freelancer.servicesN', { n: profile.total_services })}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#888' }}>
                    <Calendar size={13} />
                    <span>{t('freelancer.joined', { d: formatDate(profile.member_since) })}</span>
                  </div>
                  {profile.experience_years && profile.experience_years > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#888' }}>
                      <Award size={13} />
                      <span>{t('freelancer.expYears', { n: profile.experience_years })}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 소개 */}
            {profile.bio && (
              <div style={card}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 10 }}>{t('profile.bio')}</h2>
                <p style={{ fontSize: 14, color: '#555', lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0 }}>{profile.bio}</p>
              </div>
            )}

            {/* 스킬 */}
            {skills.length > 0 && (
              <div style={card}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 12 }}>{t('profile.skills')}</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {skills.map((s, i) => (
                    <span key={i} style={{
                      padding: '5px 13px', background: '#EEF2FF', color: INDIGO,
                      borderRadius: 100, fontSize: 13, fontWeight: 600,
                    }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* 제공 서비스 */}
            {profile.services.length > 0 && (
              <div style={card}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 14 }}>{t('freelancer.servicesOffered')}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {profile.services.map(svc => (
                    <div key={svc.id}
                      onClick={() => { console.log('[FreelancerProfile] 서비스 클릭:', svc.id); navigate(`/services/${svc.id}`); }}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 16px', background: '#FAFAFA', borderRadius: 10,
                        border: '1px solid #F0F0F0', cursor: 'pointer', transition: 'all 0.15s',
                      }}
                      onMouseOver={e => { e.currentTarget.style.background = '#F5F3FF'; e.currentTarget.style.borderColor = '#C7D2FE'; }}
                      onMouseOut={e => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#F0F0F0'; }}
                    >
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 4 }}>{svc.title}</div>
                        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#AAA' }}>
                          <span>{categoryLabel(svc.category_name)}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Clock size={11} />{t('projectDetail.daysN', { n: svc.delivery_days_basic })}
                          </span>
                          {svc.rating_average > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                              <Star size={11} fill="#F59E0B" stroke="#F59E0B" />{Number(svc.rating_average).toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: INDIGO }}>
                          {t('serviceDetail.priceFrom', { price: Number(svc.price_basic).toLocaleString() })}
                        </span>
                        <ChevronRight size={14} color="#CCC" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 리뷰 */}
            <div style={card}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 16 }}>{t('serviceDetail.reviews')}</h2>
              <ReviewList userId={profile.id} />
            </div>
          </div>

          {/* ── 오른쪽 사이드바 ── */}
          <div style={{ position: 'sticky', top: 80 }}>
            <div style={card}>
              {/* 시간당 단가 */}
              {profile.hourly_rate && profile.hourly_rate > 0 && (
                <div style={{ marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid #F5F5F5' }}>
                  <div style={{ fontSize: 12, color: '#AAA', marginBottom: 4 }}>{t('freelancer.hourlyRate')}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#111', letterSpacing: '-0.5px' }}>
                    {t('freelancer.wonPerH', { v: Number(profile.hourly_rate).toLocaleString() })}
                  </div>
                </div>
              )}

              {/* 통계 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
                {[
                  { label: t('freelancer.statServices'), value: profile.total_services },
                  { label: t('serviceDetail.reviews'), value: profile.total_reviews },
                  { label: t('serviceDetail.statRating'), value: Number(profile.average_rating || 0).toFixed(1) },
                  { label: t('freelancer.statExp'), value: profile.experience_years ? t('freelancer.yearsN', { n: profile.experience_years }) : '-' },
                ].map((s, i) => (
                  <div key={i} style={{ padding: '12px', background: '#FAFAFA', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#111', letterSpacing: '-0.3px' }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: '#BBB', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* 상담 버튼 */}
              <button onClick={handleContact} style={{
                width: '100%', padding: '13px',
                background: INDIGO, color: '#fff',
                border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                fontFamily: 'inherit',
              }}>
                <MessageCircle size={16} />{t('freelancer.contact')}
              </button>

              {/* AI 리포트 버튼 */}
              {user && profile && user.id !== profile.id && (
                <button
                  onClick={() => { console.log('[FreelancerProfile] AI 리포트 클릭:', profile.id); navigate(`/report/${profile.id}`); }}
                  style={{
                    width: '100%', padding: '11px',
                    background: 'rgba(167,139,250,0.12)',
                    color: '#a78bfa',
                    border: '1px solid rgba(167,139,250,0.3)',
                    borderRadius: 10, fontSize: 14, fontWeight: 700,
                    cursor: 'pointer', marginTop: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    fontFamily: 'inherit',
                  }}
                >
                  <FileText size={14} />{t('freelancer.aiReport')}
                </button>
              )}

              {/* AI 분석 챗봇 */}
              {user && profile && user.id !== profile.id && (
                <ExpertAnalysisChatbot expertId={profile.id} expertNickname={profile.nickname} />
              )}

              {/* 포트폴리오 링크 */}
              {profile.portfolio_url && (
                <a href={profile.portfolio_url} target="_blank" rel="noreferrer" style={{
                  display: 'block', marginTop: 10, padding: '10px',
                  background: '#F5F5F5', color: '#555', borderRadius: 9,
                  textAlign: 'center', fontSize: 13, fontWeight: 600,
                  textDecoration: 'none',
                }}>
                  {t('profile.viewPortfolio')}
                </a>
              )}

              {/* 인증 배지 */}
              {profile.insurance_verified && (
                <div
                  onClick={() => user && user.id !== profile.id ? navigate(`/report/${profile.id}`) : undefined}
                  style={{
                    marginTop: 14, padding: '10px 14px',
                    background: '#ECFDF5', borderRadius: 9,
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 12, color: '#059669', fontWeight: 600,
                    cursor: user && user.id !== profile.id ? 'pointer' : 'default',
                    border: user && user.id !== profile.id ? '1px solid #a7f3d0' : 'none',
                  }}
                >
                  <ShieldCheck size={14} />
                  <span style={{ flex: 1 }}>{t('freelancer.insuranceVerified')}</span>
                  {user && user.id !== profile.id && (
                    <span style={{ fontSize: 11, color: '#059669', opacity: 0.7 }}>{t('freelancer.checkReport')}</span>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FreelancerProfilePage;
